import { ColDef, RowClickedEvent } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { aggridHeaderClass } from 'src/components/common/common';
import { GridContextWrapper } from 'src/components/common/gridWrapper/GridContextWrapper';
import Grid_Touch from 'src/components/common/gridWrapper/Grid_Touch';
import { GridCustomColumnDefs } from 'src/utils/grid/GridCustomColumnDefs';
import { buildDefaultColumnStateFromDefs } from 'src/utils/grid/columnPinUtils';
import { useGridSettingContent } from 'src/utils/grid/GridSettingContent';
import { defaultColDef, getDefaultRowIndexColumnConfig } from 'src/utils/grid/gridStyleUtils';

import { defaultMST3040_R01HeaderDto, MST3040_R01HeaderDto } from './Dto_MST3040_R01';
import { useMST3040_R01Store } from './Store_MST3040_R01';
import { onlyTouchGridStyle } from 'src/utils/grid/gridFunctionUtil';

const COLUMN_STATE_KEY = 'MST3040_R01-grid-column-state';

interface Props {
	gridRef: React.RefObject<AgGridReact<MST3040_R01HeaderDto> | null>;
}

export const Table_MST3040_R01 = (props: Props) => {
	const { gridRef } = props;

	const {
		MST3040_R01List,
		selectedMST3040_R01,
		setSelectedMST3040_R01,
		initSelectedMST3040_R01,
		columnState,
		setColumnState,
		scrollTop, setScrollTop,
		setDetailScrollTop,
		setUsageScrollTop,
	} = useMST3040_R01Store();

	const [hoveredRowIndex, setHoveredRowIndex] = useState<number | null>(null);

	const {
		getRowId,
		settingRowIndex,
		createRowClassRules,
		paddedGridData,
		ensureSelectedRow,
	} = useGridSettingContent({
		gridRef,
		key: 'prntItemcd',
		gridData: MST3040_R01List,
		defaultGridData: defaultMST3040_R01HeaderDto,
		selectedRow: selectedMST3040_R01,
	});

	const headerNames: Record<string, string> = {
		rowIndex: '',
		prntItemcd: '품목코드',
		itemNm: '품명',
		itemSpec: '규격',
	};

	const getCellStyle = useCallback((params: unknown, key: string) => onlyTouchGridStyle(key, 'prntItemcd')(params), []);

	const baseColumnDefs: ColDef<MST3040_R01HeaderDto>[] = useMemo(() => [
		...getDefaultRowIndexColumnConfig(),
		{
			headerName: '품목코드', colId: 'prntItemcd', field: 'prntItemcd', width: 95,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'prntItemcd'),
		},
		{
			headerName: '품명', colId: 'itemNm', field: 'itemNm', width: 210,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemNm'),
		},
		{
			headerName: '규격', colId: 'itemSpec', field: 'itemSpec', width: 180,
			headerClass: aggridHeaderClass,
			cellStyle: (p) => getCellStyle(p, 'itemSpec'),
		},
	], [getCellStyle]);

	const defaultColumnState = useMemo(
		() => buildDefaultColumnStateFromDefs(baseColumnDefs, [])
	, [baseColumnDefs]);

	const columnDefs: ColDef<MST3040_R01HeaderDto>[] = useMemo(() => {
		if (columnState === null) return baseColumnDefs;
		return GridCustomColumnDefs<MST3040_R01HeaderDto>({
			baseColumnDefs,
			columnState,
			headerNames,
			fallbackHeaderClass: aggridHeaderClass,
		});
	}, [columnState, settingRowIndex, baseColumnDefs, headerNames]);

	const handleRowClick = useCallback((e: RowClickedEvent<MST3040_R01HeaderDto>) => {
		if (e.node.rowPinned === 'bottom') return;
		const rowData = e.data;
		if (!rowData) return;

		const isSelected = selectedMST3040_R01?.prntItemcd === rowData.prntItemcd;
		setSelectedMST3040_R01(isSelected ? undefined : rowData);
		setDetailScrollTop(null);
		setUsageScrollTop(null);
	}, [selectedMST3040_R01, setSelectedMST3040_R01, setDetailScrollTop, setUsageScrollTop]);

	const pinnedBottomRow = useMemo(() => ([{
		rowIndex: '',
		prntItemcd: `${MST3040_R01List.length.toLocaleString()}건`,
		editable: false,
	}]), [MST3040_R01List.length]);

	useEffect(() => { ensureSelectedRow(); }, [MST3040_R01List, selectedMST3040_R01]);

	return (
		<GridContextWrapper<MST3040_R01HeaderDto>
			gridRef={gridRef}
			paddedGridData={paddedGridData}
			initSelected={initSelectedMST3040_R01}
			defaultRowData={defaultMST3040_R01HeaderDto}
			baseColumnDefs={columnState ? columnDefs : baseColumnDefs}
			defaultColumnState={defaultColumnState}
			setColumnState={setColumnState}
			localStorageKey={COLUMN_STATE_KEY}
			menuItems={undefined}
		>
			<Grid_Touch<MST3040_R01HeaderDto>
				ref={gridRef}
				rowData={paddedGridData}
				columnDefs={columnDefs}
				defaultColDef={defaultColDef}
				getRowId={getRowId}
				rowClassRules={createRowClassRules<MST3040_R01HeaderDto>({
					idKey: 'prntItemcd',
					selectedList: selectedMST3040_R01 ? [selectedMST3040_R01.prntItemcd] : [],
					deletedList: [],
				})}
				onRowClicked={handleRowClick}
				onCellKeyDown={undefined}
				pinnedBottomRowData={pinnedBottomRow}
				setHoveredRowIndex={setHoveredRowIndex}
				columnStateKey={COLUMN_STATE_KEY}
				setColumnState={setColumnState}
				scrollTop={scrollTop}
				setScrollTop={setScrollTop}
			/>
		</GridContextWrapper>
	);
};
